import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useCreateLeafReportMutation } from "@common/utils/graphql";
import { Body } from "@hypercard/web-components";
import { useState } from "react";

interface Props {
  onSubmit: (reportId: string) => void;
}

export const CreateNewReportContent = ({ onSubmit }: Props) => {
  const [createExpenseReport, { loading }] = useCreateLeafReportMutation();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();

  const handleCreateReport = async () => {
    try {
      const report = await createExpenseReport({
        variables: {
          title,
          notes,
        },
      });

      if (
        report.data?.upsertExpenseReport.__typename !==
        "MutationUpsertExpenseReportSuccess"
      ) {
        throw new Error();
      }

      const reportId = report.data.upsertExpenseReport.data.id;
      onSubmit(String(reportId));
    } catch (e) {
      toast({
        title: "Failed to register reimbursement, please try again",
        variant: "destructive",
      });
    }
  };

  const header = (
    <SheetHeader>
      <SheetTitle>New Report</SheetTitle>
      <Body size="md" weight="medium" className="mt-2 text-white/70">
        Create new report or add to existing
      </Body>
    </SheetHeader>
  );

  const footer = (
    <div className="p-sheet-gap flex grow items-end">
      <Button
        type="submit"
        className="w-full"
        disabled={loading || !title}
        onClick={handleCreateReport}
      >
        {loading ? "Creating report..." : "Create report"}
      </Button>
    </div>
  );

  const body = (
    <div className="mx-sheet-gap mt-4 space-y-8">
      <div className="w-full space-y-3">
        <Label htmlFor="title">Title</Label>
        <Input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full"
          placeholder="Choose a title for report..."
          disabled={loading}
        />
      </div>
      <div className="space-y-3">
        <Label htmlFor="notes" className="flex justify-between">
          <span>Notes</span>
          <span>Optional</span>
        </Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
          placeholder="e.g. My expenses for our company offsite in Miami, including..."
        />
      </div>
    </div>
  );

  return (
    <>
      {header}
      {body}
      {footer}
    </>
  );
};
